import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api';
import { Switch, Route, Redirect } from 'react-router-dom';

import {
  load as loadTeam,
  update as updateTeam,
  addMember as addTeamMember,
  updateMember as updateTeamMember,
  removeMember as removeTeamMember,
} from 'js/actions/team-actions';
import {
  list as listAssessments,
  update as updateAssessment,
} from 'js/actions/assessment-actions';
import { load as loadGoals } from 'js/actions/goal-actions';
import { notify } from 'js/actions/alert-actions';
import { listConnectors } from 'js/actions/connector-actions';
import {
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
} from 'js/actions/team-connector-configuration-actions';

import { useQuery } from 'js/hooks/useQuery';
import TeamActivityContainer from './TeamActivityContainer';
import TeamSettingsContainer from './TeamSettingsContainer';
import TeamConnectorReportContainer from './TeamConnectorReportContainer';
import TeamRubricsContainer from './TeamRubricsContainer';
import NotFound from 'js/routes/NotFound';

/**
 * Higher-order container: loads common data (team, goals, assessments,
 * connectors, teamConnectorConfigurations) and passes it to the view
 * containers. Renders only a Switch—no layout or tabs; each child
 * uses TeamPageLayout + TeamTabs and its own content.
 */
function TeamDashboardShellContainer(props) {
  const {
    goals,
    team,
    assessments,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    query,
    queryId,
    loadTeam,
    loadGoals,
    match,
    listAssessments,
    listConnectors,
    listTeamConnectorConfigurations,
    setPrimaryGoal,
    updateAssessment,
    updateTeam,
    notify,
    createTeamConnectorConfiguration,
    updateTeamConnectorConfiguration,
    deleteTeamConnectorConfiguration,
  } = props;

  const { teamId, workspaceId } = match.params;

  useEffect(() => {
    loadTeam(teamId, queryId);
    loadGoals();
    listAssessments({ team_id: teamId });
  }, [
    teamId,
    workspaceId,
    queryId,
    loadTeam,
    loadGoals,
    listAssessments,
    listConnectors,
    listTeamConnectorConfigurations,
  ]);

  const refreshConnectorsQuery = useCallback(
    (queryId) => listConnectors(workspaceId, queryId),
    [workspaceId, listConnectors]
  );

  const {
    result: connectors,
    refresh: refreshConnectors,
    hasLoaded: connectorsHasLoaded,
  } = useQuery({
    queryId: `connectors-${workspaceId}`,
    query: refreshConnectorsQuery,
    schema: SCHEMAS.CONNECTOR_ARRAY,
  });

  const refreshTeamConnectorConfigurationsQuery = useCallback(
    (queryId) => listTeamConnectorConfigurations(teamId, queryId),
    [teamId, listTeamConnectorConfigurations]
  );

  const {
    result: teamConnectorConfigurations,
    refresh: refreshTeamConnectorConfigurations,
    hasLoaded: teamConnectorConfigurationsHasLoaded,
  } = useQuery({
    queryId: `teamConnectorConfigurations-${teamId}`,
    query: refreshTeamConnectorConfigurationsQuery,
    schema: SCHEMAS.TEAM_CONNECTOR_CONFIGURATION_ARRAY,
  });

  useEffect(() => refreshConnectors(), [refreshConnectors]);
  useEffect(
    () => refreshTeamConnectorConfigurations(),
    [refreshTeamConnectorConfigurations]
  );

  const handleSetPrimary = useCallback(
    (team, goal) => setPrimaryGoal(team.id, goal.id),
    [setPrimaryGoal]
  );
  const handleToggleAssessmentLock = useCallback(
    (assessment) =>
      updateAssessment({ id: assessment.id, is_locked: !assessment.is_locked }),
    [updateAssessment]
  );
  const handleDeleteAssessment = useCallback(
    (assessment) => updateAssessment({ id: assessment.id, active: false }),
    [updateAssessment]
  );
  const handleUpdateTeam = useCallback((t) => updateTeam(t), [updateTeam]);
  const handleCopyLink = useCallback(() => {
    notify({ type: 'success', message: 'Link copied to clipboard' });
  }, [notify]);

  const handleSaveConnectorConfiguration = useCallback(
    async (connectorId, configurationData, existingConfiguration = null) => {
      try {
        if (existingConfiguration) {
          await updateTeamConnectorConfiguration(teamId, connectorId, {
            configuration: configurationData,
          });
          notify({
            type: 'success',
            message: 'Connector configuration updated successfully',
          });
        } else {
          await createTeamConnectorConfiguration(teamId, {
            connector_id: connectorId,
            configuration: configurationData,
          });
          notify({
            type: 'success',
            message: 'Connector configuration created successfully',
          });
        }
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to save connector configuration',
        });
        throw error;
      }
    },
    [
      teamId,
      createTeamConnectorConfiguration,
      updateTeamConnectorConfiguration,
      notify,
    ]
  );

  const handleDeleteConnectorConfiguration = useCallback(
    async (connectorId) => {
      try {
        await deleteTeamConnectorConfiguration(teamId, connectorId);
        notify({
          type: 'success',
          message: 'Connector configuration deleted successfully',
        });
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to delete connector configuration',
        });
        throw error;
      }
    },
    [teamId, deleteTeamConnectorConfiguration, notify]
  );

  const handleTestConnectorConfiguration = useCallback(async () => {}, []);

  const basePath = `/w/:workspaceId/teams/:teamId`;

  if (query.error && query.error.status === 404) {
    return <NotFound />;
  }

  const commonLayoutProps = {
    team,
    teamConnectorConfigurations,
    teamConnectorConfigurationsHasLoaded,
  };

  return (
    <Switch>
      <Route path={`${basePath}/settings`}>
        <TeamSettingsContainer
          {...commonLayoutProps}
          onUpdateTeam={handleUpdateTeam}
          onAddTeamMember={addTeamMember}
          onUpdateTeamMember={updateTeamMember}
          onRemoveTeamMember={removeTeamMember}
          connectors={connectors}
          connectorsHasLoaded={connectorsHasLoaded}
          onSaveConnectorConfiguration={handleSaveConnectorConfiguration}
          onDeleteConnectorConfiguration={handleDeleteConnectorConfiguration}
          onTestConnectorConfiguration={handleTestConnectorConfiguration}
        />
      </Route>
      <Route path={`${basePath}/connectors/:connectorComponent/:connectorId`}>
        <TeamConnectorReportContainer {...commonLayoutProps} />
      </Route>
      <Route exact path={`${basePath}/rubrics`}>
        <TeamRubricsContainer {...commonLayoutProps} />
      </Route>
      <Route exact path={`${basePath}/dashboard`}>
        <TeamActivityContainer
          {...commonLayoutProps}
          goals={goals}
          assessments={assessments}
          onSetPrimary={handleSetPrimary}
          onToggleAssessmentLock={handleToggleAssessmentLock}
          onDeleteAssessment={handleDeleteAssessment}
          onCopyLink={handleCopyLink}
        />
      </Route>
      <Route path={basePath}>
        <Redirect to={`${basePath}/dashboard`} />
      </Route>
    </Switch>
  );
}

export default connect(
  (store, props) => {
    const { match } = props;
    const { entities } = store;
    let { teamId, workspaceId } = match.params;
    teamId = +teamId;
    workspaceId = +workspaceId;
    const queryId = `w/${workspaceId}/t/${teamId}`;
    let team = entities.teams[teamId];
    if (!!team && team.workspace_id !== workspaceId) {
      team = undefined;
    }

    return {
      queryId,
      query: store.queries[queryId] || {},
      team,
      goals: [],
      assessments: orderBy(
        Object.values(entities.assessments).filter(
          (a) => a.team_id === teamId && a.active
        ),
        ['name', 'inserted_at'],
        ['asc', 'desc']
      ).map((a) => denormalize(a, SCHEMAS.ASSESSMENT, entities)),
    };
  },
  {
    loadGoals,
    loadTeam,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    setPrimaryGoal: (teamId, goalId) =>
      updateTeam({ id: teamId, primary_goal: goalId }),
    updateTeam,
    listAssessments,
    updateAssessment,
    notify,
    listConnectors,
    listTeamConnectorConfigurations,
    createTeamConnectorConfiguration,
    updateTeamConnectorConfiguration,
    deleteTeamConnectorConfiguration,
  }
)(TeamDashboardShellContainer);
